import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { images } from "../assets/images";
import { BackgroundMedia } from "./BackgroundMedia";

export function CallToActionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  return (
    <section
      id="join-us"
      ref={ref}
      className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden"
    >
      {/* 背景图片 */}
      <div className="absolute inset-0">
        <BackgroundMedia
          videoSrc={images.cta}
          imageSrc={images.ctaImage}
          className="w-full h-full object-cover"
        />
        {/* 轻微遮罩 - 仅保持文字可读性 */}
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* 动态背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute top-20 left-20 w-96 h-96 bg-white/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute bottom-20 right-20 w-80 h-80 bg-amber-300/20 rounded-full blur-3xl"
        />
      </div>

      {/* 主内容 */}
      <div className="relative z-10 text-center px-6 max-w-4xl">
        {/* 标题 */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16"
        >
          {/* 主标题 */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 1.2, delay: 0.8 }}
            className="text-white text-xl md:text-3xl lg:text-4xl mb-12 leading-tight"
          >
            与我们同行
            <br />
            点亮善意的灯塔
          </motion.h2>

          {/* 装饰线 */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 1.2, delay: 1.2, ease: "easeOut" }}
            className="h-px bg-gradient-to-r from-transparent via-white/60 to-transparent mb-12"
          />

          {/* 项目信息 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="space-y-6"
          >
            <div className="inline-block bg-white/10 backdrop-blur-lg px-10 py-5 rounded-full border border-white/30">
              <p className="text-white/90 text-xl md:text-3xl lg:text-4xl tracking-wide">
                克来姆公益咖啡与茶
              </p>
            </div>

            {/* 使命宣言 */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 1, delay: 1.8 }}
              className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
            >
              每一份善意，都将汇聚成照亮他们未来的光
            </motion.p>
          </motion.div>
        </motion.div>
      </div>

      {/* 光芒效果 */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={isInView ? { opacity: 0.3, scale: 1 } : { opacity: 0, scale: 0 }}
        transition={{ duration: 2, delay: 1 }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] pointer-events-none"
      >
        <div className="absolute inset-0 bg-gradient-radial from-orange-300/40 via-transparent to-transparent rounded-full blur-3xl" />
      </motion.div>

      {/* 底部装饰波浪 */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 120"
          className="w-full h-24 fill-white/10"
          preserveAspectRatio="none"
        >
          <motion.path
            d="M0,60 C240,100 480,20 720,60 C960,100 1200,20 1440,60 L1440,120 L0,120 Z"
            animate={{
              d: [
                "M0,60 C240,100 480,20 720,60 C960,100 1200,20 1440,60 L1440,120 L0,120 Z",
                "M0,80 C240,40 480,100 720,60 C960,20 1200,80 1440,60 L1440,120 L0,120 Z",
                "M0,60 C240,100 480,20 720,60 C960,100 1200,20 1440,60 L1440,120 L0,120 Z",
              ],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </svg>
      </div>
    </section>
  );
}

